import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Home, ExternalLink, TrendingUp, TrendingDown } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { fmt, tooltipStyle } from "./oversiktConstants";
import { dec, num } from "@/lib/formatters";

const COMPONENTS = [
  { key: "rente", label: "Rentekostnader" },
  { key: "vedlikehold", label: "Vedlikehold" },
  { key: "energi", label: "Energi" },
  { key: "kommunale_avgifter", label: "Kommunale avgifter" },
  { key: "forsikring", label: "Forsikring" },
  { key: "eiendomsskatt", label: "Eigedomsskatt" },
];

// data: { kommune, fylke_avg, land_avg, national_share_series, source }
const BokostnadSection = ({ data, medianHouseholdIncome, incomeLabel = "median samla inntekt per hushald, før skatt", householdTypes, fylkeName, rentMonthly, rentEstimate }) => {
  const k = data?.kommune;
  if (!k) {
    return (
      <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="bokostnad-section">
        <CardHeader className="border-b border-[#E6E9F0] pb-4">
          <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
            <Home className="w-5 h-5 text-[#103766]" />
            Bokostnad
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="h-[120px] flex items-center justify-center text-[#8AA4BF]" data-testid="bokostnad-empty">
            Bokostnadsdata blir henta – last sidan på nytt om litt
          </div>
        </CardContent>
      </Card>
    );
  }

  const fylkeLabel = fylkeName ? `Snitt ${fylkeName}` : "Fylke";
  const utanFylke = !data.fylke_avg;
  const compareData = [
    { name: "Totalt", Kommune: k.total, ...(utanFylke ? {} : { [fylkeLabel]: data.fylke_avg?.total ?? null }), Landet: data.land_avg?.total ?? null },
    ...COMPONENTS.map((c) => ({
      name: c.label,
      Kommune: k.components?.[c.key] ?? 0,
      ...(utanFylke ? {} : { [fylkeLabel]: data.fylke_avg?.components?.[c.key] ?? null }),
      Landet: data.land_avg?.components?.[c.key] ?? null,
    })),
  ];

  const kommuneShare = medianHouseholdIncome ? (k.total / medianHouseholdIncome) * 100 : null;
  const rentShare = medianHouseholdIncome && rentMonthly ? ((rentMonthly * 12) / medianHouseholdIncome) * 100 : null;
  const series = (data.national_share_series || []).map((s) => ({
    year: s.year,
    Landet: s.forecast ? null : s.share,
    Prognose: s.forecast ? s.share : null,
  }));
  // connect forecast line to last actual point
  const lastActual = [...series].reverse().find((s) => s.Landet != null);
  if (lastActual) lastActual.Prognose = lastActual.Landet;
  const nationalNow = (data.national_share_series || []).filter((s) => !s.forecast).slice(-1)[0];

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="bokostnad-section" id="bokostnad">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Home className="w-5 h-5 text-[#103766]" />
          Bokostnad {k.year}
        </CardTitle>
        <p className="text-sm text-[#4B647F]">Huseiernes bokostnadsindeks – årlege kostnader for ein standard einebustad på 120 m² (kr)</p>
      </CardHeader>
      <CardContent className="pt-6 space-y-8">
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
          <div className="xl:col-span-2 space-y-4">
            <div className="p-4 bg-[#F6F8FB] rounded-lg">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">Bokostnad per år</p>
              <p className="text-3xl font-light text-[#1F2A36]" data-testid="bokostnad-total">{fmt(k.total)} kr</p>
              {k.pct_change != null && (
                <p className={`text-sm mt-1 flex items-center gap-1 ${k.pct_change > 0 ? "text-[#D35E4E]" : "text-[#3F8A86]"}`}>
                  {k.pct_change > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {k.pct_change > 0 ? "+" : ""}{dec(k.pct_change, 1)} % frå {k.prev_year} ({fmt(k.prev_total)} kr)
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {!utanFylke && (
                <div className="p-3 border border-[#E6E9F0] rounded-lg">
                  <p className="text-xs text-[#8AA4BF]">{fylkeLabel}</p>
                  <p className="text-lg font-light text-[#1F2A36]" data-testid="bokostnad-fylke">{fmt(data.fylke_avg?.total)} kr</p>
                </div>
              )}
              <div className="p-3 border border-[#E6E9F0] rounded-lg">
                <p className="text-xs text-[#8AA4BF]">Landet ({data.land_avg?.count} kommunar)</p>
                <p className="text-lg font-light text-[#1F2A36]" data-testid="bokostnad-land">{fmt(data.land_avg?.total)} kr</p>
              </div>
            </div>
            <ul className="text-sm space-y-1" data-testid="bokostnad-components">
              {COMPONENTS.map((c) => (
                <li key={c.key} className="flex justify-between">
                  <span className="text-[#4B647F]">{c.label}</span>
                  <span className="tabular-nums text-[#1F2A36]">{k.components?.[c.key] != null ? `${fmt(k.components[c.key])} kr` : "Ingen"}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="xl:col-span-3">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-3">Kommunen mot {utanFylke ? "landet" : "fylke og landet"} (kr per år)</p>
            <div className="h-[380px]" data-testid="bokostnad-compare-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={compareData} layout="vertical" barGap={1} barCategoryGap="22%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis type="number" stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="name" stroke="#8AA4BF" fontSize={12} width={150} interval={0} />
                  <Tooltip formatter={(v) => `${fmt(v)} kr`} contentStyle={tooltipStyle} />
                  <Legend />
                  <Bar dataKey="Kommune" fill="#103766" radius={[0, 4, 4, 0]} />
                  {!utanFylke && <Bar dataKey={fylkeLabel} fill="#8AA4BF" radius={[0, 4, 4, 0]} />}
                  <Bar dataKey="Landet" fill="#E8A34A" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6" data-testid="bokostnad-share">
          <div className="xl:col-span-2 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF]">Kor stor del av inntekten går til å bu?</p>
            <div className="p-4 bg-[#F6F8FB] rounded-lg">
              <p className="text-xs text-[#8AA4BF]">Kommunen – bokostnad delt på median hushaldsinntekt</p>
              <p className="text-3xl font-light text-[#1F2A36]" data-testid="bokostnad-share-kommune">
                {kommuneShare != null ? `${dec(kommuneShare, 1)} %` : "—"}
              </p>
              {medianHouseholdIncome && (
                <p className="text-xs text-[#4B647F] mt-1">{fmt(k.total)} kr av {fmt(medianHouseholdIncome)} kr ({incomeLabel})</p>
              )}
            </div>
            {rentShare != null && (
              <div className="p-4 border border-[#E6E9F0] rounded-lg" data-testid="bokostnad-share-rent">
                <p className="text-xs text-[#8AA4BF]">Å leige ein toromsbustad{rentEstimate ? " (prissone anslått)" : ""}</p>
                <p className="text-2xl font-light text-[#1F2A36]">{dec(rentShare, 1)} %</p>
                <p className="text-xs text-[#4B647F] mt-1">{fmt(rentMonthly * 12)} kr i årleg leige delt på {incomeLabel} (SSB leigemarknadsundersøkinga)</p>
              </div>
            )}
            {nationalNow && (
              <div className="p-4 border border-[#E6E9F0] rounded-lg">
                <p className="text-xs text-[#8AA4BF]">Landet {nationalNow.year} (Huseierne)</p>
                <p className="text-2xl font-light text-[#1F2A36]" data-testid="bokostnad-share-land">{dec(nationalNow.share, 1)} %</p>
                <p className="text-xs text-[#4B647F] mt-1">Gjennomsnittleg andel av inntekta til hushaldet brukt på bokostnader</p>
              </div>
            )}
          </div>
          <div className="xl:col-span-3">
            {householdTypes?.length > 0 && (
              <div className="mb-6" data-testid="bokostnad-share-types">
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">Andel av inntekt etter skatt per hushaldstype</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-[#8AA4BF]">
                      <th className="text-left font-medium pb-1">Hushaldstype</th>
                      <th className="text-right font-medium pb-1">Median inntekt etter skatt</th>
                      <th className="text-right font-medium pb-1">Eige (bokostnad)</th>
                      {rentMonthly && <th className="text-right font-medium pb-1">Leige 2-roms</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {householdTypes.map((t) => (
                      <tr key={t.key} data-testid={`bokostnad-share-type-${t.key}`}>
                        <td className="py-0.5 text-[#1F2A36]">{t.label}</td>
                        <td className="text-right tabular-nums text-[#4B647F]">{fmt(t.income)} kr</td>
                        <td className={`text-right tabular-nums font-medium ${(k.total / t.income) * 100 > 40 ? "text-[#D35E4E]" : "text-[#1F2A36]"}`}>{dec(((k.total / t.income) * 100), 1)} %</td>
                        {rentMonthly && <td className="text-right tabular-nums text-[#1F2A36]">{dec((((rentMonthly * 12) / t.income) * 100), 1)} %</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[11px] text-[#8AA4BF] mt-1">Raudt = over 40 % av inntekten. Kjelde: SSB 06944 (median inntekt etter skatt per hushaldstype).</p>
              </div>
            )}
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-3">Bokostnader som andel av inntekt, heile landet (%)</p>
            <div className="h-[260px]" data-testid="bokostnad-share-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series}>
                  {endringsLinjer(series)}
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                  <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                  <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${num(v)} %`} domain={[0, "auto"]} />
                  <Tooltip formatter={(v) => `${num(v)} %`} contentStyle={tooltipStyle} />
                  <Legend />
                  {kommuneShare != null && (
                    <ReferenceLine y={kommuneShare} stroke="#103766" strokeDasharray="4 4" label={{ value: `Kommunen ${dec(kommuneShare, 1)} %`, fill: "#103766", fontSize: 11, position: "insideTopLeft" }} />
                  )}
                  <Line type="monotone" dataKey="Landet" stroke="#E8A34A" strokeWidth={2} dot={{ fill: "#E8A34A", r: 3 }} connectNulls={false} />
                  <Line type="monotone" dataKey="Prognose" stroke="#E8A34A" strokeWidth={2} strokeDasharray="5 5" dot={{ fill: "white", stroke: "#E8A34A", r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <p className="text-xs text-[#8AA4BF] text-center">
          Kjelde:{" "}
          <a href={data.source} target="_blank" rel="noopener noreferrer" className="text-[#103766] hover:underline inline-flex items-center gap-1">
            Huseiernes bokostnadsindeks <ExternalLink className="w-3 h-3" />
          </a>
          {" "}(Samfunnsøkonomisk analyse). Andel for kommunen er berekna av oss med SSB-inntektsdata og er ikkje direkte samanliknbar med Huseiernes landstal.
        </p>
      </CardContent>
    </Card>
  );
};

export default BokostnadSection;
